import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, Train, ArrowRight, Calendar, Users, Clock, MapPin, Hash } from "lucide-react";
import QRCode from "../components/QRCode";
import { toolautosubmitEnabled } from "@/lib/webmcpConfig";

export default function ConfirmationPage() {
  const params = new URLSearchParams(window.location.search);
  const rawData = params.get("data");

  const train = useMemo(() => {
    if (!rawData) return null;
    try {
      return JSON.parse(decodeURIComponent(rawData));
    } catch {
      return null;
    }
  }, [rawData]);

  if (!train) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <p className="text-lg font-medium">No ticket data found</p>
          <Link to="/">
            <Button className="mt-4">Back to Search</Button>
          </Link>
        </div>
      </div>
    );
  }

  const bookingRef = train.id.replace("train-", "").toUpperCase().slice(0, 8);
  const qrData = `SBB-TICKET-${bookingRef}-${train.origin}-${train.destination}-${train.date}-${Date.now()}`;

  const handleNewSearch = (e) => {
    e.preventDefault();

    const event = e.nativeEvent;
    if (typeof event.respondWith === "function" && event.agentInvoked) {
      event.respondWith({
        ok: true,
        resultsUrl: "/",
      });
    }

    window.location.assign("/");
  };

  const handlePrint = (e) => {
    e.preventDefault();

    const event = e.nativeEvent;
    if (typeof event.respondWith === "function" && event.agentInvoked) {
      event.respondWith({
        ok: true,
        message: "Print requested",
      });
    }

    window.print();
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8 md:py-14">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
            <CheckCircle className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Booking Confirmed!</h1>
          <p className="text-muted-foreground">Your train ticket has been booked successfully</p>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-muted/50 flex flex-col items-center py-8 px-6">
            <QRCode data={qrData} size={180} />
          </div>

          <Separator />

          <div className="p-6 space-y-5">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <p className="text-2xl font-bold">{train.departureTime}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {train.origin}
                </p>
              </div>
              <div className="flex flex-col items-center px-4">
                <span className="text-xs text-muted-foreground">{train.duration}</span>
                <div className="flex items-center gap-1 my-1">
                  <div className="h-[2px] w-12 bg-border" />
                  <ArrowRight className="w-4 h-4 text-primary" />
                </div>
                <span className="text-xs text-muted-foreground">
                  {train.stops === 0 ? "Direct" : `${train.stops} stop${train.stops > 1 ? "s" : ""}`}
                </span>
              </div>
              <div className="flex-1 text-right">
                <p className="text-2xl font-bold">{train.arrivalTime}</p>
                <p className="text-sm text-muted-foreground flex items-center justify-end gap-1.5 mt-0.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {train.destination}
                </p>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <DetailItem icon={<Train className="w-4 h-4" />} label="Train" value={train.trainNumber} />
              <DetailItem icon={<Hash className="w-4 h-4" />} label="Operator" value={train.operator} />
              <DetailItem icon={<Calendar className="w-4 h-4" />} label="Date" value={train.date} />
              <DetailItem icon={<Users className="w-4 h-4" />} label="Passengers" value={train.passengers} />
              <DetailItem icon={<Clock className="w-4 h-4" />} label="Platform" value={train.platform} />
              <DetailItem
                icon={<Hash className="w-4 h-4" />}
                label="Trip Type"
                value={train.tripType === "round-trip" ? "Round trip" : "One way"}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Price per person ({train.tripType === "round-trip" ? "round trip" : "one way"})
                </span>
                <span>CHF {train.pricePerPerson.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Passengers</span>
                <span>× {train.passengers}</span>
              </div>
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-primary">CHF {train.totalPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <form
            onSubmit={handleNewSearch}
            toolname="newSearch"
            tooldescription="Return to the train search page."
        {...(toolautosubmitEnabled ? { toolautosubmit: "" } : {})}
            className="flex-1"
          >
            <Button type="submit" variant="outline" className="w-full h-11">
              New Search
            </Button>
          </form>

          <form
            onSubmit={handlePrint}
            toolname="printTicket"
            tooldescription="Print the confirmed train ticket."
            {...(toolautosubmitEnabled ? { toolautosubmit: "" } : {})}
            className="flex-1"
          >
            <Button type="submit" className="w-full h-11">
              Print Ticket
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 text-muted-foreground">{icon}</div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
    </div>
  );
}